import type { Moneda } from '../api/types';

const LOCALE = 'es-VE';

export function formatInteger(value: number): string {
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 }).format(value);
}

export function formatDecimal(value: number, digits = 1): string {
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

export function formatRate(value: number): string {
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

const SYMBOLS: Record<Moneda, string> = {
  USD: '$',
  EUR: '€',
  VES: 'Bs.',
};

/**
 * La operación es en bolívares. Los importes se guardan en USD como respaldo,
 * pero toda la interfaz se presenta en VES; estas tasas se actualizan desde
 * CurrencyProvider con las tasas BCV vigentes.
 */
let displayUsdRate = 36.42;
let displayEurRate = 39.87;

export function setDisplayRates(usdRate: number, eurRate: number): void {
  if (Number.isFinite(usdRate) && usdRate > 0) displayUsdRate = usdRate;
  if (Number.isFinite(eurRate) && eurRate > 0) displayEurRate = eurRate;
}

export function getDisplayRates(): { usd: number; eur: number } {
  return { usd: displayUsdRate, eur: displayEurRate };
}

/** Formatea un monto sin conversión; se usa para valores que ya están en VES. */
export function formatMoneyRaw(value: number, currency: Moneda = 'VES'): string {
  const amount = new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  return `${SYMBOLS[currency]} ${amount}`;
}

/**
 * Convierte el monto de su moneda de origen a bolívares para mostrarlo.
 * `USD` representa el respaldo en dólares de la transacción.
 */
export function formatCompactRaw(value: number): string {
  const compact = new Intl.NumberFormat(LOCALE, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
  return `Bs. ${compact}`;
}

export function formatMoney(value: number, currency: Moneda = 'USD'): string {
  const valueBs =
    currency === 'VES'
      ? value
      : currency === 'EUR'
        ? value * displayEurRate
        : value * displayUsdRate;
  return formatMoneyRaw(valueBs, 'VES');
}

export function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Caracas',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function addDaysIso(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Caracas',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * Las fechas de PostgreSQL llegan como `YYYY-MM-DD`. `new Date('...')` las
 * interpreta como UTC y en Caracas retroceden un día; se ancla a mediodía UTC
 * para conservar la fecha calendario.
 */
function parseFecha(iso: string): Date {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso)
    ? new Date(`${iso}T12:00:00Z`)
    : new Date(iso);
}

export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'America/Caracas',
  })
    .format(parseFecha(iso))
    .replace('.', '');
}

export function formatDateShort(iso: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: 'short',
    timeZone: 'America/Caracas',
  })
    .format(parseFecha(iso))
    .replace('.', '');
}

export function formatDateLong(date = new Date()): string {
  const formatted = new Intl.DateTimeFormat(LOCALE, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'America/Caracas',
  }).format(date);
  return formatted.replace('.', '');
}

export function formatTime(iso: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Caracas',
  }).format(new Date(iso));
}

export function formatRelative(iso: string | null): string {
  if (!iso) return 'nunca';
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.max(0, Math.round(diff / 60000));
  if (minutes < 1) return 'hace un momento';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  return formatDate(iso);
}

export function greeting(date = new Date()): string {
  const hour = Number(
    new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      hour12: false,
      timeZone: 'America/Caracas',
    }).format(date),
  );
  if (hour < 12) return 'Buenos días';
  if (hour < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

export const ESTADO_LABEL: Record<string, string> = {
  borrador: 'Borrador',
  en_cola: 'En cola',
  enviada: 'Enviada',
  procesada: 'Procesada',
  rechazada: 'Rechazada',
  emitida: 'Emitida',
  pagada: 'Pagada',
  parcial: 'Parcial',
  vencida: 'Vencida',
  anulada: 'Anulada',
  registrada: 'Registrada',
  RECIBIDA: 'Recibida',
  PARCIAL: 'Parcial',
  PAGADA: 'Pagada',
  ANULADA: 'Anulada',
  BORRADOR: 'Borrador',
  aceptada: 'Aceptada',
};

export function estadoTone(estado: string): 'success' | 'warning' | 'danger' | 'neutral' {
  if (estado === 'procesada' || estado === 'aceptada' || estado === 'pagada' || estado === 'PAGADA') return 'success';
  if (estado === 'enviada' || estado === 'en_cola' || estado === 'emitida' || estado === 'parcial' || estado === 'PARCIAL' || estado === 'RECIBIDA') return 'warning';
  if (estado === 'rechazada' || estado === 'anulada' || estado === 'vencida' || estado === 'ANULADA') return 'danger';
  return 'neutral';
}

export function roleLabel(rol: string): string {
  const labels: Record<string, string> = {
    admin: 'Administrador',
    supervisor: 'Supervisor',
    ventas: 'Ventas',
    cobranza: 'Cobranzas',
    auditor: 'Auditor',
  };
  return labels[rol] ?? rol;
}
